<template>
  <v-card>
    <v-card-title class="pb-0">
      <h1 class="headline">
        Редактирование Сотрудника
      </h1>
    </v-card-title>
    <v-card-text class="pt-2">
      <v-form v-model="isValid">
        <v-layout column>
          <v-flex xs12>
            <v-layout row>
              <v-flex xs6>
                <TitleTextField
                  v-model="firstName"
                  :required="true"
                  :rules="[rules.required, rules.min]"
                  label="Имя*"
                  class="mx-2 mb-3"
                  @input="isChanged = true"
                />
              </v-flex>
              <v-flex xs6>
                <TitleTextField
                  v-model="lastName"
                  :required="true"
                  :rules="[rules.required, rules.min]"
                  label="Фамилия*"
                  class="mx-2 mb-3"
                  @input="isChanged = true"
                />
              </v-flex>
              <v-flex xs6>
                <app-reference
                  v-model="user"
                  class="mx-2 mb-3"
                  resource="users"
                  component-name="User"
                  label="Пользователь"
                  :outline="true"
                  :height-field="44"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <app-reference
                  v-model="office"
                  class="mx-2 mb-3"
                  resource="offices"
                  component-name="Office"
                  label="Офис*"
                  :outline="true"
                  height-field="44"
                  :rules="[rules.required]"
                  @input="isChanged = true"
                />
              </v-flex>
              <v-flex xs6>
                <TitleTextField
                  v-model="specialization"
                  label="Специализация"
                  class="mx-2 mb-3"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>
            <v-flex xs12>
              <MultipleReferenceCompact
                v-model="positions"
                class="mx-2 mb-3"
                resource="positions"
                component-name="Position"
                label="Должность*"
                :outline="true"
                @input="isChanged = true"
              />
            </v-flex>
            <v-layout row>
              <v-flex xs6>
                <EditableList
                  v-model="phones"
                  class="mx-2 mb-3"
                  label="Телефон*"
                  :mask="getMask('phone')"
                  :hint="hint"
                  filter="phoneInternal"
                  outline
                  title="Редактирование номеров телефонов"
                  data-test="phone"
                  @change="changePhones"
                />
              </v-flex>
              <v-flex xs6>
                <TitleTextField
                  v-model="email"
                  class="mx-2 mb-3"
                  label="Email*"
                  :rules="[rules.email,rules.empty]"
                  outline
                  height="52"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs12>
                <app-reference-list
                  v-model="directions"
                  class="mx-2 mb-3"
                  label="Направления"
                  :columns="[{ title: 'Наименование', name: 'title' }]"
                  search-placeholder="Страна"
                  resource="countries"
                  reference-component="Country"
                  data-test="direction"
                  @change="isChanged=true"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>

        <v-layout class="mt-3">
          <v-btn
            small
            color="primary"
            :disabled="disabledButton || loading"
            @click="submit(false)"
          >
            Сохранить
          </v-btn>
          <v-btn
            v-if="!isModal"
            small
            color="primary"
            :disabled="disabledButton || loading"
            @click="saveAndClose"
          >
            Сохранить и закрыть
          </v-btn>
          <v-btn
            small
            @click="cancel"
          >
            Отмена
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="id!==0"
            small
            :color="isRemoved ? 'success' :'error'"
            @click="changeRemovedState"
          >
            {{ isRemoved ? "Восстановить" : "Удалить" }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import ConfirmMixin from "./../../mixins/confirm-cancel-changes.js";
import { NestedEditorsEventBus } from "./../../components/nested-editors/nested-editors-event-bus.js";
import * as employeeApi from "./../../backend/employeeApi.js";

export default {
  mixins: [ConfirmMixin],
  props: ["id"],
  data() {
    return {
      isValid: false,
      isChanged: false,
      rules: {
        empty: (v) => !!v || "",
        required: (v) => !!v || "Поле обязательно к заполнению",
        min: (v) =>
          v.length >= 2 || "Поле должно содержать не менее 2-х символов",
        email: (v) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(v) || v === "" || "Не корректно введен e-mail.";
        },
      },
      resource: "employees",
      addMessage: "Сотрудник добавлен",
      editMessage: "Сотрудник изменен",
      firstName: "",
      lastName: "",
      user: null,
      phones: [],
      email: "",
      office: null,
      positions: [],
      specialization: "",
      directions: [],
      isRemoved: false,
      hint: "Номер телефона уже добавлен",
      loading: false,
    };
  },
  computed: {
    isModal() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options._componentTag === "NestedEditors") {
          return true;
        }
        parent = parent.$parent;
      }
      return false;
    },
    disabledButton() {
      return (
        !(this.isValid && this.isChanged) ||
        !this.phones ||
        this.phones.length == 0 ||
        this.isRemoved
      );
    },
  },
  created() {
    if (+this.id) {
      this.getData();
    }
  },
  mounted() {
    if (this.isModal) {
      NestedEditorsEventBus.$emit("modal-editor-mounted");
    }
  },
  methods: {
    async getData() {
      const res = await employeeApi.getEmployee(this.resource, this.id);
      this.firstName = res.data.FirstName;
      this.lastName = res.data.LastName;
      this.user = res.data.User
        ? {
          id: res.data.User.Id,
          title: res.data.User.Title,
        }
        : null;
      this.phones = res.data.Phones;
      this.email = res.data.Email;
      this.office = {
        id: res.data.Office.Id,
        title: res.data.Office.Title,
      };
      this.positions = res.data.Positions.map((item) => {
        return {
          id: item.Id,
          title: item.Title,
        };
      });
      this.specialization = res.data.Specialization;
      this.directions = res.data.Directions.map((item) => {
        return {
          id: item.Id,
          title: item.Title,
        };
      });
      this.isRemoved = res.data.IsRemoved;
    },
    async submit(closeAfterSubmit) {
      const employee = {
        firstName: this.firstName,
        lastName: this.lastName,
        userId: this.user ? this.user.id : null,
        phones: this.phones,
        email: this.email,
        officeId: this.office.id,
        positions: this.positions.map((item) => {
          return item.id;
        }),
        specialization: this.specialization,
        directions: this.directions.map((item) => {
          return item.id;
        }),
      };

      if (this.id && this.id > 0) {
        this.loading = true;
        await employeeApi.updateEmployee(this.resource, this.id, employee);
        this.showInfo(this.editMessage);
        this.isChanged = false;
        this.loading = false;
        if (closeAfterSubmit) {
          this.$router.go(-1);
        }
        this.getData();
        if (this.isModal) {
          NestedEditorsEventBus.$emit("create", this.id);
        }
      } else {
        this.loading = true;
        const res = await employeeApi.createEmployee(this.resource, employee);
        this.showInfo(this.addMessage);
        this.isChanged = false;
        this.loading = false;
        if (this.isModal) {
          NestedEditorsEventBus.$emit("create", res.data);
        } else {
          this.$router.replace(`/${this.resource}/${res.data}`);
          if (closeAfterSubmit) {
            this.$router.go(-1);
          }
        }
      }
    },
    cancel() {
      if (this.isModal) {
        NestedEditorsEventBus.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    async saveAndClose() {
      await this.submit(true);
    },
    changePhones(val) {
      this.isChanged = true;
      this.phones = val;
    },
    async changeRemovedState() {
      await employeeApi.changeRemovedState(
        this.resource,
        this.id,
        this.isRemoved,
      );
      this.isRemoved = !this.isRemoved;
    },
    getMask(controlType) {
      switch (controlType) {
        case "phone":
          return "+##(###)###-##-## *#####";
        default:
          return "";
      }
    },
    resetLoaders() {
      this.loading = false;
    },
  },
};
</script>
